import { get, pickBy } from "lodash";
import { isNonEmptyArray, isNonEmptyObject } from "@cayuse/shared-react.utils.type-checks";

import {
	CAYUSE_TYPES,
	DEFAULT_BUDGET_PERIODS,
	MEMBER_TYPES,
	READ_ONLY_BUDGET_CATEGORIES,
} from "globals/constants";
import store from "store/store";
import {
	awardSelectors,
	budgetBuilderSelectors,
	proposalSelectors,
	tenantSelectors,
} from "store/selectors";
import { convertToLookupTable, isPresent, toNumber } from "utils/budgetBuilder";


export const getResearchTeamElement = (budgetType, payload) => {
	const { formElements } = budgetType === "proposal" ? proposalSelectors.proposal() : awardSelectors.award();

	if (!formElements) {
		return [];
	}

	for (let formElement of formElements) {
		if (formElement.cayuseType === CAYUSE_TYPES.RESEARCH_TEAM) {
			const path = formElement.value.includes(`${budgetType}Content`)
				? formElement.value
				: `root.${budgetType}Content.${formElement.value}`;

			return get(payload, path);
		}
	}
};

export const setDefaultValuesOnPayload = (payload, fromRoot) => {
	if (fromRoot && !isPresent(payload.root?.budgetBuilderContent?.budgetPeriods)) {
		if (!isPresent(payload.root?.budgetBuilderContent)) {
			payload.root.budgetBuilderContent = {};
		}
		payload.root.budgetBuilderContent.budgetPeriods = DEFAULT_BUDGET_PERIODS;
	}

	let budgetBuilderContent = fromRoot ? payload?.root?.budgetBuilderContent : payload?.budgetBuilderContent;

	if (!budgetBuilderContent?.budgetPeriods) {
		budgetBuilderContent.budgetPeriods = DEFAULT_BUDGET_PERIODS;
	}
	if (!budgetBuilderContent?.sponsoredCostsByPeriod) {
		let sponsoredCostsByPeriod = {};
		for (let periodRow of budgetBuilderContent.budgetPeriods.periodRows) {
			sponsoredCostsByPeriod[periodRow.periodNumber] = getDefaultLineItemsFromState(periodRow?.rateForPeriod);
		}
		budgetBuilderContent.sponsoredCostsByPeriod = sponsoredCostsByPeriod;
	}
	if (fromRoot) {
		payload.root.budgetBuilderContent = budgetBuilderContent;
	} else {
		payload.budgetBuilderContent = budgetBuilderContent;
	}

	return payload;
};

const teamMemberHasParentMemberWithAssociation = (teamMember, parentAssociationsById) => {
	return teamMember.type !== MEMBER_TYPES.MEMBER_PARENT ||
		parentAssociationsById?.[teamMember.member]?.find(assoc => assoc.association === teamMember.association);
};

export const removeUnusedParentMembersFromBudgetBuilder = (payload, parentMembers) => {
	const researchTeamByPeriod = payload?.root?.budgetBuilderContent?.researchTeamByPeriod;
	if (isNonEmptyObject(researchTeamByPeriod) && isNonEmptyArray(parentMembers)) {
		const parentAssociationsById = convertToLookupTable(parentMembers, "member", "associations");

		for (let periodNumber in researchTeamByPeriod) {
			const researchTeamForPeriod = researchTeamByPeriod?.[periodNumber];
			if (isNonEmptyArray(researchTeamForPeriod)) {
				researchTeamByPeriod[periodNumber] = researchTeamForPeriod.filter(
					(item) => teamMemberHasParentMemberWithAssociation(item, parentAssociationsById)
				);
			}
		}
	}

	return payload;
};

export const mapCaseInstanceData = (response, { budgetType, fromRoot, currentPeriodNumber }) => {
	const fromParent = getResearchTeamElement(budgetType, response);
	const defaultValuesPayload = setDefaultValuesOnPayload(response, fromRoot);
	const payload = removeUnusedParentMembersFromBudgetBuilder(defaultValuesPayload, fromParent);

	const bbContent = fromRoot ? payload?.root?.budgetBuilderContent : payload?.budgetBuilderContent;

	const researchTeamFromBudgetBuilder = bbContent?.researchTeamByPeriod?.[currentPeriodNumber];
	const unnamed = bbContent?.researchTeamUnnamedMembers || [];

	const currentPeriod = bbContent?.budgetPeriods?.periodRows?.find(row => row.periodNumber === currentPeriodNumber);
	const currentPeriodRate = currentPeriod?.rateForPeriod || 0;
	let sponsoredCosts = bbContent?.sponsoredCostsByPeriod?.[currentPeriodNumber];
	if (!isPresent(sponsoredCosts)) {
		sponsoredCosts = getDefaultLineItemsFromState(currentPeriodRate);
	}

	const externalCostShare = bbContent?.externalCostShareByPeriod?.[currentPeriodNumber] || [];
	const internalCostShare = bbContent?.internalCostShareByPeriod?.[currentPeriodNumber] || [];
	const versions = bbContent?.versions;

	return {
		payload,
		researchTeamFromBudgetBuilder,
		fromParent,
		unnamed,
		currentPeriod,
		versions,
		sponsoredCosts,
		externalCostShare,
		internalCostShare,
	};
};

export const getDefaultLineItemsFromState = faRateAmount => {
	const categories = tenantSelectors.proposalCategories();

	return getDefaultLineItems(categories, faRateAmount);
};

const getExtendedCategory = (category, faRateAmount) => {
	return {
		//real data attributes
		active: category.active,
		code: category.code,
		hash: category.hash,
		sort: category.sort,
		value: category.value,
		items: [],
		//extended attributes
		isFA: false,
		FA: faRateAmount,
		directCost: 0,
		isExpanded: true,
	};
};

export const getDefaultLineItems = (categories = [], faRateAmount) => {
	return categories
		.filter(category => !READ_ONLY_BUDGET_CATEGORIES.includes(category.value))
		.reduce((acc, category) => {
			if (category.active) {
				const extendedCategory = getExtendedCategory(category, faRateAmount);

				if (category.items) {
					category.items.forEach(item => {
						if (item.active) {
							extendedCategory.items.push(getExtendedCategory(item, faRateAmount));
						}
					});
				}

				acc.push(extendedCategory);
			}

			return acc;
		}, []);
};

export const buildUpLookupList = (researchTeamMembers = []) => {
	return researchTeamMembers.reduce((acc, member) => {
		if (member.type === MEMBER_TYPES.MEMBER_UNNAMED) {
			acc[member.id] = member;
		} else {
			acc[member.member] = member;
		}

		return acc;
	}, {});
};

const findMemberAssociations = (member, budgetBuilderMembers) => {
	const foundAssociations = [];

	const foundBudgetBuilderMembers = (budgetBuilderMembers ?? [])
		.filter(bbMember => {
			const memberFoundWithNewAssociation = bbMember.member === member.member
				&& !foundAssociations.includes(bbMember.association);

			if (memberFoundWithNewAssociation) {
				foundAssociations.push(bbMember.association);
			}

			return memberFoundWithNewAssociation;
		});

	if (foundBudgetBuilderMembers.length === member.associations.length) {
		return null;
	}

	const associations = member.associations.filter(assoc => !foundAssociations.includes(assoc.association));

	return {
		...member,
		associations,
	};
};

export const filterAndChangeMembersType = (parentMembers = [], budgetBuilderMembers) => {
	return parentMembers
		.map(member => findMemberAssociations(member, budgetBuilderMembers))
		.filter(member => member)
		.map(member => ({ ...member, type: MEMBER_TYPES.MEMBER_PARENT }));
};

export const findRoleInRoles = (optionId, roles) => roles?.find(({ id }) => id === optionId)?.text || "";

export const filterKeysLessThan = (object, maxKeyNumber) => pickBy(
	object,
	(value, key) => toNumber(key) <= maxKeyNumber,
);

export const prepareSetupPayload = (setupData, state = store.getState()) => {
	const payload = budgetBuilderSelectors.payload(state);
	const unnamedMembers = budgetBuilderSelectors.savedUnnamedMembers(state);
	const internalCostShareByPeriod = budgetBuilderSelectors.internalCostShareByPeriod(state);
	const externalCostShareByPeriod = budgetBuilderSelectors.externalCostShareByPeriod(state);
	const sponsoredCostsByPeriod = budgetBuilderSelectors.sponsoredCostsByPeriod(state);
	const researchTeamByPeriod = budgetBuilderSelectors.researchTeamByPeriod(state);

	const numberOfPeriods = setupData.budgetPeriods.numberOfPeriods;

	let newInternalCostShareByPeriod = filterKeysLessThan(internalCostShareByPeriod, numberOfPeriods);
	let newExternalCostShareByPeriod = filterKeysLessThan(externalCostShareByPeriod, numberOfPeriods);
	let newSponsoredCostsByPeriod = filterKeysLessThan(sponsoredCostsByPeriod, numberOfPeriods);
	let newResearchTeamByPeriod = filterKeysLessThan(researchTeamByPeriod, numberOfPeriods);

	let budgetPeriods = setupData.budgetPeriods;

	if (setupData.copyToFrom && isNonEmptyArray(setupData.copyToFrom)) {
		for (let copyToFrom of setupData.copyToFrom) {
			if (isPresent(copyToFrom?.from) && isPresent(copyToFrom?.to) && copyToFrom?.from !== copyToFrom?.to) {
				const fromKey = copyToFrom?.from;
				const toKey = copyToFrom?.to;
				const fromBudgetPeriod = budgetPeriods.periodRows[fromKey - 1];
				const originalBudgetPeriod = budgetPeriods.periodRows[toKey - 1];
				let newPeriodRow = {
					...fromBudgetPeriod,
					periodNumber: toKey,
					startDate: originalBudgetPeriod.startDate,
					endDate: originalBudgetPeriod.endDate,
				};
				let newPeriodRows = [ ...budgetPeriods.periodRows ];
				newPeriodRows[toKey - 1] = newPeriodRow;
				budgetPeriods.periodRows = newPeriodRows;
				newInternalCostShareByPeriod = {
					...newInternalCostShareByPeriod,
					[toKey]: newInternalCostShareByPeriod[fromKey],
				};
				newExternalCostShareByPeriod = {
					...newExternalCostShareByPeriod,
					[toKey]: newExternalCostShareByPeriod[fromKey],
				};
				newSponsoredCostsByPeriod = {
					...newSponsoredCostsByPeriod,
					[toKey]: newSponsoredCostsByPeriod[fromKey],
				};
				newResearchTeamByPeriod = {
					...newResearchTeamByPeriod,
					[toKey]: newResearchTeamByPeriod[fromKey],
				};
			}
		}
	}

	return {
		...payload,
		root: {
			...payload?.root,
			budgetBuilderContent: {
				...payload?.root?.budgetBuilderContent,
				...setupData,
				budgetPeriods,
				externalCostShareByPeriod: newExternalCostShareByPeriod,
				internalCostShareByPeriod: newInternalCostShareByPeriod,
				sponsoredCostsByPeriod: newSponsoredCostsByPeriod,
				researchTeamByPeriod: newResearchTeamByPeriod,
				researchTeamUnnamedMembers: unnamedMembers,
			},
		},
	};
};

export const getPreparedPayload = (state = store.getState()) => {
	const payload = budgetBuilderSelectors.payload(state);
	const currentPeriodNumber = budgetBuilderSelectors.currentPeriodNumber(state);
	const researchTeamMembers = budgetBuilderSelectors.researchTeamMembers(state);
	const unnamedMembers = budgetBuilderSelectors.unnamedMembers(state);
	const savedUnnamedMembers = budgetBuilderSelectors.savedUnnamedMembers(state);
	const currentPeriod = budgetBuilderSelectors.currentPeriod(state);
	const budgetPeriods = budgetBuilderSelectors.budgetPeriods(state);
	const sponsoredCosts = budgetBuilderSelectors.sponsoredCosts(state);
	const externalCostShare = budgetBuilderSelectors.externalCostShare(state);
	const internalCostShare = budgetBuilderSelectors.internalCostShare(state);
	const periodRows = budgetBuilderSelectors.periodRows(state);

	let newPeriodRows = [ ...periodRows ];
	for (let i = 0; i < newPeriodRows.length; i++) {
		let periodRow = newPeriodRows[i];
		if (periodRow.periodNumber === currentPeriod.periodNumber) {
			newPeriodRows[i] = currentPeriod;
			break;
		}
	}

	const unnamedMembersToSave = isNonEmptyArray(savedUnnamedMembers) ? [ ...savedUnnamedMembers ] : [];
	for (const unnamedMember of unnamedMembers) {
		const existingUnnamedMemberIndex = unnamedMembersToSave.findIndex(newUnnamedMember => newUnnamedMember.id === unnamedMember.id);
		if (existingUnnamedMemberIndex < 0) {
			unnamedMembersToSave.push({
				...unnamedMember,
				sponsoredCosts: null,
				costShare: null,
			});
		} else {
			unnamedMembersToSave[existingUnnamedMemberIndex] = {
				...unnamedMembersToSave[existingUnnamedMemberIndex],
				...unnamedMember,
				sponsoredCosts: null,
				costShare: null,
			};
		}
	}

	return {
		...payload,
		root: {
			...payload?.root,
			budgetBuilderContent: {
				...payload?.root?.budgetBuilderContent,
				budgetPeriods: {
					...budgetPeriods,
					periodRows: newPeriodRows,
				},
				externalCostShareByPeriod: {
					...payload?.root?.budgetBuilderContent?.externalCostShareByPeriod,
					[currentPeriodNumber]: externalCostShare,
				},
				internalCostShareByPeriod: {
					...payload?.root?.budgetBuilderContent?.internalCostShareByPeriod,
					[currentPeriodNumber]: internalCostShare,
				},
				sponsoredCostsByPeriod: {
					...payload?.root?.budgetBuilderContent?.sponsoredCostsByPeriod,
					[currentPeriodNumber]: sponsoredCosts,
				},
				researchTeamByPeriod: {
					...payload?.root?.budgetBuilderContent?.researchTeamByPeriod,
					[currentPeriodNumber]: researchTeamMembers,
				},
				researchTeamUnnamedMembers: unnamedMembersToSave,
			},
		},
	};
};
